/**
 * SplashScreen.tsx
 *
 * Full-screen loading overlay shown on page load.
 *
 * ## Behaviour
 * - `splashMode === 0` — Shows the app title and subtitle only (default).
 * - `splashMode === 1` — Shows the app title plus an animated spinning wheel
 *   and a "Shuffling…" label while the deck is being randomised.
 * - `splashMode === 2` — Skips the splash screen entirely; returns `null`
 *   immediately without rendering anything. Used when navigating back to the
 *   calendar after the hand-scan ritual so the title page is not shown.
 * - Closes when the parent calls `onDone()` (which triggers a 600 ms fade-out),
 *   or automatically after `maxDurationMs` milliseconds as a safety fallback.
 *
 * ## Props
 * @prop splashMode     - 0 = title only, 1 = title + shuffling spinner, 2 = skip entirely.
 * @prop onDone         - Callback invoked after the fade-out animation completes.
 * @prop maxDurationMs  - Maximum time in ms before auto-closing (default 10 000).
 */

'use client';

import React, { useEffect, useState } from 'react';
import packageJson from '../../../package.json';
import { preloadVoskModel } from '@/lib/modelLoader';

/**
 * generateDateGrammar
 *
 * Placeholder function that generates a Vosk grammar JSON string
 * for date recognition. The grammar restricts the recognizer to only
 * accept date-related words (months, ordinals, numbers).
 *
 * TODO: Replace this placeholder with a full grammar generator that
 * produces a Vosk-compatible grammar JSON with all date word combinations.
 *
 * @returns JSON string suitable for passing to the Vosk KaldiRecognizer
 */
function generateDateGrammar(): string {
    const months = [
        "january", "february", "march", "april", "may", "june", 
        "july", "august", "september", "october", "november", "december"
    ];
    
    // Ordinal numbers 1-31 (with alternatives)
		const days = {
				1:  ["first", "one"],
				2:  ["second", "two"],
				3:  ["third", "three"],
				4:  ["fourth", "four"],
				5:  ["fifth", "five"],
				6:  ["sixth", "six"],
				7:  ["seventh", "seven"],
				8:  ["eighth", "eight"],
				9:  ["ninth", "nine"],
				10: ["tenth", "ten"],
				11: ["eleventh", "eleven"],
				12: ["twelfth", "twelve"],
				13: ["thirteenth", "thirteen"],
				14: ["fourteenth", "fourteen"],
				15: ["fifteenth", "fifteen"],
				16: ["sixteenth", "sixteen"],
				17: ["seventeenth", "seventeen"],
				18: ["eighteenth", "eighteen"],
				19: ["nineteenth", "nineteen"],
				20: ["twentieth", "twenty"],
				21: ["twenty first", "twenty one"],
				22: ["twenty second", "twenty two"],
				23: ["twenty third", "twenty three"],
				24: ["twenty fourth", "twenty four"],
				25: ["twenty fifth", "twenty five"],
				26: ["twenty sixth", "twenty six"],
				27: ["twenty seventh", "twenty seven"],
				28: ["twenty eighth", "twenty eight"],
				29: ["twenty ninth", "twenty nine"],
				30: ["thirtieth", "thirty"],
				31: ["thirty first", "thirty one"]
		};
		let grammar = [];

		months.forEach(month => {
				for (let i = 1; i <= 31; i++) {
						const [ordinal, cardinal] = days[i];

						// May the first
						grammar.push(`${month} the ${ordinal}`);

						// First of May
						grammar.push(`${ordinal} of ${month}`);

						// May first
						grammar.push(`${month} ${ordinal}`);

						// May one
						grammar.push(`${month} ${cardinal}`);
				}
		});

    //return grammar;
    return JSON.stringify({ type: 'date', grammar });
}


/** Session storage key for the date grammar */
const GRAMMAR_SESSION_KEY = 'vosk-date-grammar';

/**
 * Controls the visual mode of the splash screen.
 *
 * - `0` — Show the app title and subtitle only (no spinner).
 * - `1` — Show the app title with an animated shuffling spinner.
 * - `2` — Skip the splash screen entirely; render nothing.
 */
export type SplashMode = 0 | 1 | 2;

interface SplashScreenProps {
  /** 0 = title only | 1 = title + shuffling spinner | 2 = skip entirely */
  splashMode: SplashMode;
  onDone: () => void;
  /** Maximum time (ms) before auto-closing even if not explicitly dismissed */
  maxDurationMs?: number;
}

/**
 * SplashScreen
 *
 * - splashMode=0 → shows title only (no spinner).
 * - splashMode=1 → shows title + animated spinning wheel + "Shuffling…" text.
 * - splashMode=2 → renders nothing; skips the splash entirely.
 * - Closes when onDone() is called by the parent, or after maxDurationMs (default 10 s).
 */
export default function SplashScreen({
  splashMode,
  onDone,
  maxDurationMs = 10000,
}: SplashScreenProps) {
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  // Generate grammar and store in session on mount, then pre-load Vosk model
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const grammar = generateDateGrammar();
      sessionStorage.setItem(GRAMMAR_SESSION_KEY, grammar);
      // Pre-load Vosk model in background (fire-and-forget)
      preloadVoskModel(grammar);
    } catch {
      // ignore storage errors
    }
  }, []);

  // Mode 2: skip entirely — call onDone immediately and render nothing
  useEffect(() => {
    if (splashMode === 2) {
      onDone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splashMode]);

  // Safety timeout — always close after maxDurationMs
  useEffect(() => {
    if (splashMode === 2) return;
    const timer = setTimeout(() => {
      triggerClose();
    }, maxDurationMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDurationMs, splashMode]);

  function triggerClose() {
    setFadeOut(true);
    setTimeout(() => {
      setVisible(false);
      onDone();
    }, 600); // fade-out duration
  }

  // Mode 2 or already hidden: render nothing
  if (splashMode === 2 || !visible) return null;

  return (
    <div
      className={`
        fixed inset-0 z-[9999] flex flex-col items-center justify-center
        bg-background parchment-texture
        transition-opacity duration-600
        ${fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}
      `}
      aria-live="polite"
      aria-label="Loading TarotCal"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative flex flex-col items-center gap-6 px-8 text-center">
        {/* Decorative top star */}
        <span className="text-primary text-3xl animate-gold-pulse select-none" aria-hidden>✦</span>

        {/* App title */}
        <h1 className="font-decorative text-3xl sm:text-4xl text-primary tracking-widest uppercase">
          A Tarot a Day
        </h1>

        {/* Subtitle */}
        <p className="font-body text-muted-foreground text-sm tracking-wider italic">
          The Oracle of Days
        </p>

        <div className="arcane-divider w-48 sm:w-64 my-1" />

        {/* Spinner + label — only when shuffling (mode 1) */}
        {splashMode === 1 && (
          <div className="flex flex-col items-center gap-3 mt-2">
            {/* CSS-animated spinning wheel */}
            <div className="relative w-16 h-16" aria-hidden>
              {/* Outer ring */}
              <div
                className="absolute inset-0 rounded-full border-4 border-primary/20"
                style={{ borderTopColor: 'var(--color-primary, #c9a84c)' }}
              />
              {/* Spinning arc */}
              <div
                className="absolute inset-0 rounded-full border-4 border-transparent"
                style={{
                  borderTopColor: 'var(--color-primary, #c9a84c)',
                  borderRightColor: 'rgba(201,168,76,0.4)',
                  animation: 'splash-spin 1s linear infinite',
                }}
              />
              {/* Inner star */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-primary text-lg animate-gold-pulse select-none">✦</span>
              </div>
            </div>

            <p className="font-display text-xs text-primary tracking-widest uppercase animate-pulse">
              Shuffling…
            </p>
          </div>
        )}

        {/* Bottom decorative star */}
        <span className="text-primary/40 text-xl select-none mt-2" aria-hidden>✦</span>
      </div>

      {/* Version number */}
      <div className="absolute bottom-4 w-full text-center">
        <span className="font-body text-muted-foreground/40 text-xs tracking-wider">
          v{packageJson.version}
        </span>
      </div>

      {/* Keyframe for the spinner */}
      <style>{`
        @keyframes splash-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .duration-600 { transition-duration: 600ms; }
      `}</style>
    </div>
  );
}
